import { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  HelpCircle,
  Book,
  Video,
  MessageCircle,
  ChevronRight,
  Search,
  LayoutDashboard,
  Briefcase,
  Users,
  Package,
  Layers,
  Calendar,
  FileText,
  DollarSign,
  Target,
  Wrench,
  Settings,
  Play,
  ExternalLink
} from 'lucide-react';

// Help content based on user role
const getHelpContent = (role) => {
  const commonSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Play,
      articles: [
        {
          title: 'Welcome to Jax Portal',
          content: 'The Jax Portal is your central hub for managing all aspects of your remodeling projects. From here, you can track jobs, manage teams, handle documents, and much more.',
          steps: [
            'Log in with your provided credentials',
            'Review your Dashboard for an overview',
            'Navigate using the left sidebar menu',
            'Use the top-right menu for account settings'
          ]
        },
        {
          title: 'Navigating the Dashboard',
          content: 'Your dashboard provides a quick overview of all active projects, upcoming deadlines, and key metrics.',
          steps: [
            'View active jobs count and status breakdown',
            'Check recent activity feed',
            'Monitor team availability',
            'Review financial summaries (COO only)'
          ]
        }
      ]
    },
    {
      id: 'jobs',
      title: 'Jobs Pipeline',
      icon: Briefcase,
      articles: [
        {
          title: 'Creating a New Job',
          content: 'Start tracking a new project by creating a job entry with all relevant details.',
          steps: [
            'Click "New Job" button in Jobs Pipeline',
            'Enter client information and project type',
            'Set estimated value and timeline',
            'Add description and special notes',
            'Save to create the job'
          ]
        },
        {
          title: 'Managing Job Status',
          content: 'Update job status as projects progress through different phases.',
          steps: [
            'Open the job detail page',
            'Use the status dropdown to change status',
            'Available statuses: Pursuing, Active, Completed, On Hold',
            'Add notes when changing status for documentation'
          ]
        },
        {
          title: 'Sharing with Clients',
          content: 'Generate secure links to share project status with clients.',
          steps: [
            'Open the job detail page',
            'Click "Share with Client" button',
            'Copy the generated link',
            'Send link to client via email or message',
            'Clients can view progress without logging in'
          ]
        }
      ]
    },
    {
      id: 'floor-plans',
      title: 'Floor Plans & 3D',
      icon: Layers,
      articles: [
        {
          title: 'Uploading Floor Plans',
          content: 'Upload scanned or digital floor plans to visualize and annotate.',
          steps: [
            'Navigate to Floor Plans page',
            'Select the job from dropdown',
            'Click "Upload Plan" button',
            'Choose image file (JPG, PNG)',
            'Enter name and description',
            'Click upload to save'
          ]
        },
        {
          title: 'Using 2D Layers',
          content: 'Add electrical, plumbing, and other elements to your floor plans.',
          steps: [
            'Select a floor plan from the tabs',
            'Choose a layer (Electrical, Plumbing, etc.)',
            'Select element type (outlet, pipe, light)',
            'Click "Start Placing" button',
            'Click on the floor plan to place elements',
            'Toggle layer visibility with eye icons'
          ]
        },
        {
          title: 'Generating 3D Renders',
          content: 'Create photorealistic room visualizations using AI.',
          steps: [
            'Select a floor plan',
            'Click "Generate 3D" button',
            'Choose room type and style',
            'Select finishes (flooring, cabinets, etc.)',
            'Use "Auto-Fill from SOW" if available',
            'Click Generate and wait 30-60 seconds',
            'View render in 3D Render tab'
          ]
        }
      ]
    },
    {
      id: 'calendar',
      title: 'Calendar & Scheduling',
      icon: Calendar,
      articles: [
        {
          title: 'Adding Events',
          content: 'Schedule jobs, maintenance, meetings, and deadlines.',
          steps: [
            'Navigate to Calendar page',
            'Click "Add Event" button',
            'Select event type',
            'Enter title, date, and time',
            'Link to job or equipment if applicable',
            'Save the event'
          ]
        },
        {
          title: 'Google Calendar Sync',
          content: 'Export your calendar to Google Calendar for mobile access.',
          steps: [
            'Click "Google Sync" button',
            'Copy the ICS subscription URL',
            'Open Google Calendar settings',
            'Add calendar by URL',
            'Events will sync automatically'
          ]
        }
      ]
    },
    {
      id: 'documents',
      title: 'Documents',
      icon: FileText,
      articles: [
        {
          title: 'Creating Documents',
          content: 'Generate contracts, change orders, and other documents.',
          steps: [
            'Navigate to Documents page',
            'Click "New Document" or "From Template"',
            'Select document category',
            'Link to a job',
            'Enter content or use AI generation',
            'Save and optionally share with client'
          ]
        }
      ]
    },
    {
      id: 'settings',
      title: 'Account Settings',
      icon: Settings,
      articles: [
        {
          title: 'Updating Your Profile',
          content: 'Change your name, email, and job title.',
          steps: [
            'Navigate to Settings page',
            'Click Profile tab',
            'Update your information',
            'Click Save Changes'
          ]
        },
        {
          title: 'Changing Password',
          content: 'Update your password for security.',
          steps: [
            'Navigate to Settings page',
            'Click Security tab',
            'Enter current password',
            'Enter new password twice',
            'Click Change Password'
          ]
        }
      ]
    }
  ];

  const cooOnlySections = [
    {
      id: 'accounting',
      title: 'Accounting Suite',
      icon: DollarSign,
      articles: [
        {
          title: 'Financial Overview',
          content: 'Track revenue, expenses, and profit margins.',
          steps: [
            'Navigate to Accounting page',
            'View summary cards for key metrics',
            'Filter by date range',
            'Export reports as needed'
          ]
        },
        {
          title: 'Managing Invoices',
          content: 'Create and track invoices for jobs.',
          steps: [
            'Click Invoices tab in Accounting',
            'Click New Invoice',
            'Select job and enter line items',
            'Set payment terms',
            'Save and send to client'
          ]
        }
      ]
    },
    {
      id: 'business-plan',
      title: 'Business Planning',
      icon: Target,
      articles: [
        {
          title: 'Setting Goals',
          content: 'Define revenue, job, and growth targets.',
          steps: [
            'Navigate to Business Plan page',
            'Review current metrics',
            'Set quarterly or annual goals',
            'Track progress over time'
          ]
        }
      ]
    }
  ];

  const clientSections = [
    {
      id: 'project-overview',
      title: 'Your Project',
      icon: LayoutDashboard,
      articles: [
        {
          title: 'Viewing Project Status',
          content: 'Track your remodel progress in real-time.',
          steps: [
            'Log in to the Client Portal',
            'View the progress bar for overall completion',
            'Review project phases and tasks',
            'Check estimated completion date'
          ]
        },
        {
          title: 'Understanding Phases',
          content: 'Your project is divided into phases, each with specific tasks.',
          steps: [
            'Click on phase tabs to view details',
            'Green checkmarks indicate completed tasks',
            'Blue indicates work in progress',
            'Gray shows upcoming tasks'
          ]
        }
      ]
    },
    {
      id: 'documents',
      title: 'Shared Documents',
      icon: FileText,
      articles: [
        {
          title: 'Accessing Documents',
          content: 'View contracts, change orders, and other shared documents.',
          steps: [
            'Documents appear in the sidebar',
            'Click to view or download',
            'Contact us if a document is missing'
          ]
        }
      ]
    },
    {
      id: 'contact',
      title: 'Getting Help',
      icon: MessageCircle,
      articles: [
        {
          title: 'Contacting Our Team',
          content: 'We\'re here to help with any questions.',
          steps: [
            'Use the phone number in the sidebar',
            'Email us directly',
            'We typically respond within 24 hours'
          ]
        }
      ]
    }
  ];

  if (role === 'client') {
    return clientSections;
  }
  
  if (role === 'coo') {
    return [...commonSections, ...cooOnlySections];
  }
  
  return commonSections;
};

export const Help = () => {
  const { user } = useAuth();
  const role = user?.role || 'coo';
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState(null);
  const [activeArticle, setActiveArticle] = useState(null);

  const helpSections = getHelpContent(role);

  const filteredSections = searchTerm
    ? helpSections.map(section => ({
        ...section,
        articles: section.articles.filter(
          article =>
            article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            article.content.toLowerCase().includes(searchTerm.toLowerCase())
        )
      })).filter(section => section.articles.length > 0)
    : helpSections;

  const getRoleTitle = () => {
    switch (role) {
      case 'coo': return 'Owner/COO';
      case 'president': return 'Owner/President';
      case 'client': return 'Client';
      default: return 'User';
    }
  };

  return (
    <div className="space-y-6" data-testid="help-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl flex items-center gap-3">
            <HelpCircle className="w-8 h-8 text-[#C6A87C]" />
            Help Center
          </h1>
          <p className="text-gray-500 mt-1">
            Guide for {getRoleTitle()} - Find answers and learn how to use the portal
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search help articles..."
          className="w-full pl-12 pr-4 py-4 border border-gray-200 focus:border-[#C6A87C] focus:outline-none text-lg"
          data-testid="help-search"
        />
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <a href="#" className="p-4 bg-white border border-gray-100 hover:border-[#C6A87C] transition-colors flex items-center gap-3">
          <Book className="w-5 h-5 text-[#C6A87C]" />
          <span className="font-medium">Documentation</span>
        </a>
        <a href="#" className="p-4 bg-white border border-gray-100 hover:border-[#C6A87C] transition-colors flex items-center gap-3">
          <Video className="w-5 h-5 text-[#C6A87C]" />
          <span className="font-medium">Video Tutorials</span>
        </a>
        <a href="mailto:support@jaxremodelingsolutions.com" className="p-4 bg-white border border-gray-100 hover:border-[#C6A87C] transition-colors flex items-center gap-3">
          <MessageCircle className="w-5 h-5 text-[#C6A87C]" />
          <span className="font-medium">Contact Support</span>
        </a>
        <button
          onClick={() => {
            localStorage.removeItem(`onboarding_complete_${role}`);
            window.location.reload();
          }}
          className="p-4 bg-white border border-gray-100 hover:border-[#C6A87C] transition-colors flex items-center gap-3 text-left"
        >
          <Play className="w-5 h-5 text-[#C6A87C]" />
          <span className="font-medium">Restart Tour</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sections List */}
        <div className="lg:col-span-1 space-y-2">
          {filteredSections.map((section) => (
            <button
              key={section.id}
              onClick={() => {
                setActiveSection(section);
                setActiveArticle(section.articles[0]);
              }}
              className={`w-full p-4 text-left border transition-colors flex items-center gap-3 ${
                activeSection?.id === section.id
                  ? 'border-[#C6A87C] bg-[#C6A87C]/5'
                  : 'border-gray-100 bg-white hover:border-gray-200'
              }`}
            >
              <section.icon className={`w-5 h-5 ${activeSection?.id === section.id ? 'text-[#C6A87C]' : 'text-gray-400'}`} />
              <div className="flex-1">
                <p className="font-medium">{section.title}</p>
                <p className="text-sm text-gray-500">{section.articles.length} articles</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>
          ))}
        </div>

        {/* Article Content */}
        <div className="lg:col-span-2">
          {activeSection ? (
            <div className="bg-white border border-gray-100">
              {/* Article Tabs */}
              <div className="border-b border-gray-100 p-4 flex gap-2 overflow-x-auto">
                {activeSection.articles.map((article, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveArticle(article)}
                    className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                      activeArticle?.title === article.title
                        ? 'bg-[#C6A87C] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {article.title}
                  </button>
                ))}
              </div>

              {/* Article Content */}
              {activeArticle && (
                <motion.div
                  key={activeArticle.title}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-6"
                >
                  <h2 className="font-heading text-2xl mb-4">{activeArticle.title}</h2>
                  <p className="text-gray-600 mb-6 leading-relaxed">{activeArticle.content}</p>

                  {activeArticle.steps && (
                    <div className="bg-gray-50 p-4">
                      <h3 className="font-semibold mb-3">Steps:</h3>
                      <ol className="space-y-3">
                        {activeArticle.steps.map((step, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <span className="w-6 h-6 bg-[#C6A87C] text-white text-sm flex items-center justify-center rounded-full flex-shrink-0">
                              {idx + 1}
                            </span>
                            <span className="text-gray-700">{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          ) : (
            <div className="bg-white border border-gray-100 p-12 text-center">
              <HelpCircle className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <h3 className="font-heading text-xl text-gray-400 mb-2">Select a Topic</h3>
              <p className="text-gray-400">Choose a section from the left to view help articles</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
